// Avatar (009 revision 6, FR-044; shadcn/ui `avatar`, restyled): a person's picture in a circle, on Radix Avatar: the
// image shows once it has loaded, and the fallback (initials) shows until then and when it fails, so a broken image
// is never drawn. Sizes 32 (`sm`), 40 and 48 px (`lg`).
import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar as AvatarPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn';

/** The avatar's classes by size. */
export const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: { size: { sm: 'size-8', default: 'size-10', lg: 'size-12' } },
  defaultVariants: { size: 'default' },
});

/** Props of {@link Avatar}. */
export type AvatarProps = ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>;

/**
 * The avatar's circle.
 * @param props the size and Radix Avatar root props
 * @returns the circle
 */
export function Avatar({ className, size = 'default', ...props }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ size }), className)}
      {...props}
    />
  );
}

/**
 * The picture (give `alt` the person's name, or `""` when a name is next to it).
 * @param props Radix Avatar image props
 * @returns the picture once it has loaded
 */
export function AvatarImage({ className, ...props }: ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full object-cover', className)}
      {...props}
    />
  );
}

/**
 * What shows without a picture (initials).
 * @param props Radix Avatar fallback props
 * @returns the fallback
 */
export function AvatarFallback({
  className,
  ...props
}: ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary',
        className,
      )}
      {...props}
    />
  );
}
